import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { LanguageProvider } from './i18n';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Structures from './pages/Structures';
import Users from './pages/Users';
import Subscriptions from './pages/Subscriptions';
import Billing from './pages/Billing';
import AppCatalog from './pages/AppCatalog';
import SuperAdmin from './pages/SuperAdmin';
import Settings from './pages/Settings';
import CGU from './pages/CGU';
import Confidentialite from './pages/Confidentialite';
import MentionsLegales from './pages/MentionsLegales';
import AppDetail from './pages/AppDetail';

const PublicRoute = ({ children }) => {
  return children;
};

const PublicOrDashboard = () => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nexus-600"></div>
    </div>
  );
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
};

function App() {
  return (
    <ErrorBoundary>
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<PublicOrDashboard />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/cgu" element={<PublicRoute><CGU /></PublicRoute>} />
          <Route path="/confidentialite" element={<PublicRoute><Confidentialite /></PublicRoute>} />
          <Route path="/mentions-legales" element={<PublicRoute><MentionsLegales /></PublicRoute>} />
          <Route path="/apps/:appCode" element={<PublicRoute><AppDetail /></PublicRoute>} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/structures"
            element={
              <ProtectedRoute requiredRole={['admin_structure', 'super_admin']}>
                <Layout><Structures /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRole={['admin_structure', 'super_admin']}>
                <Layout><Users /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute requiredRole={['admin_structure', 'super_admin']}>
                <Layout><Subscriptions /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute requiredRole={['admin_structure', 'super_admin']}>
                <Layout><Billing /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps"
            element={
              <ProtectedRoute>
                <Layout><AppCatalog /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute requiredRole={['super_admin']}>
                <Layout><SuperAdmin /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout><Settings /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
